/**
 * useCalendar Hook
 *
 * Fetches calendar data for a host's listing: bookings, blocked dates
 * (manual + iCal-synced) and custom per-date pricing overrides.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc as docRef,
  getDoc,
  documentId,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../../../../services/firebase';
import { formatDate } from '../../../../utils/dates';

export interface CalendarDay {
  date: Date;
  listingId: string;
  isAvailable: boolean;
  isBlocked: boolean;
  blockedReason?: string;
  bookingId?: string;
  bookingStatus?: 'pending' | 'confirmed';
  guestName?: string;
  isCheckIn?: boolean;
  isCheckOut?: boolean;
  basePrice: number;
  customPrice?: number;
  isWeekend: boolean;
  minNights: number;
}

interface BlockedInfo {
  reason: string;
  source?: string;
  note?: string;
}

// Helper functions
const startOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const endOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const eachDayOfInterval = (start: Date, end: Date): Date[] => {
  const days: Date[] = [];
  const current = new Date(start);
  while (current <= end) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const toDate = (val: unknown): Date => {
  if (val instanceof Timestamp) return val.toDate();
  return new Date(val as string | number | Date);
};

const dateKey = (d: Date): string => formatDate(d, 'numeric');

export const useCalendar = (listingId: string, month: Date) => {
  const [data, setData] = useState<CalendarDay[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    if (!listingId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCalendarData = async () => {
      setIsLoading(true);
      try {
        const start = startOfMonth(month);
        const end = endOfMonth(month);
        const days = eachDayOfInterval(start, end);
        const startKey = dateKey(start);
        const endKey = dateKey(end);

        // Listing pricing defaults
        const listingDoc = await getDoc(docRef(db, 'listings', listingId));
        const listingData = listingDoc.exists() ? listingDoc.data() : null;

        const nightlyRate = listingData?.pricing?.nightlyRate || 0;
        const weekendRate = listingData?.pricing?.weekendRate || nightlyRate;
        const listingMinNights = listingData?.minNights || 1;

        // Bookings - rules require the host filter, so include it.
        const hostId = auth.currentUser?.uid;
        let bookings: Array<{
          id: string;
          guestName: string;
          status: string;
          checkIn: Date;
          checkOut: Date;
        }> = [];
        if (hostId) {
          try {
            const q = query(
              collection(db, 'bookings'),
              where('listingId', '==', listingId),
              where('hostId', '==', hostId),
              where('status', 'in', ['pending', 'confirmed'])
            );
            const snap = await getDocs(q);
            bookings = snap.docs.map((d) => {
              const b = d.data();
              return {
                id: d.id,
                guestName: b.guestName || 'Guest',
                status: b.status || 'pending',
                checkIn: toDate(b.checkIn),
                checkOut: toDate(b.checkOut),
              };
            });
          } catch (err) {
            console.error('Failed to fetch bookings for calendar:', err);
          }
        }

        // Blocked dates (manual + ical_sync + booked mirrors)
        const blockedMap = new Map<string, BlockedInfo>();
        try {
          const blockedSnap = await getDocs(
            collection(db, 'listings', listingId, 'blockedDates')
          );
          blockedSnap.forEach((d) => {
            const b = d.data();
            const blockStart = toDate(b.startDate);
            const blockEnd = toDate(b.endDate); // exclusive
            const current = new Date(blockStart);
            // Legacy docs where end <= start still block the start night.
            if (blockEnd <= blockStart) {
              blockedMap.set(dateKey(blockStart), {
                reason: b.reason || 'blocked',
                source: b.source,
                note: b.note,
              });
              return;
            }
            while (current < blockEnd) {
              blockedMap.set(dateKey(current), {
                reason: b.reason || 'blocked',
                source: b.source,
                note: b.note,
              });
              current.setDate(current.getDate() + 1);
            }
          });
        } catch (err) {
          console.error('Failed to fetch blocked dates:', err);
        }

        // Custom pricing for the visible month (doc IDs are YYYY-MM-DD)
        const pricingMap = new Map<string, { price?: number; minNights?: number }>();
        try {
          const pricingSnap = await getDocs(
            query(
              collection(db, 'listings', listingId, 'customPricing'),
              where(documentId(), '>=', startKey),
              where(documentId(), '<=', endKey)
            )
          );
          pricingSnap.forEach((d) => {
            const p = d.data();
            pricingMap.set(d.id, { price: p.price, minNights: p.minNights });
          });
        } catch (err) {
          console.error('Failed to fetch custom pricing:', err);
        }

        const calendarData = days.map((date) => {
          const key = dateKey(date);
          const isWeekend = [5, 6].includes(date.getDay());
          const basePrice = isWeekend ? weekendRate : nightlyRate;

          const booking = bookings.find(
            (b) => date >= b.checkIn && date < b.checkOut
          );
          const blocked = booking ? undefined : blockedMap.get(key);
          const custom = pricingMap.get(key);

          let blockedReason: string | undefined;
          if (blocked) {
            if (blocked.note) blockedReason = blocked.note;
            else if (blocked.reason === 'ical_sync')
              blockedReason = blocked.source ? `Synced: ${blocked.source}` : 'Synced calendar';
            else if (blocked.reason === 'manual') blockedReason = 'Blocked by you';
            else blockedReason = blocked.reason;
          }

          return {
            date,
            listingId,
            isAvailable: !booking && !blocked,
            isBlocked: !!blocked,
            blockedReason,
            bookingId: booking?.id,
            bookingStatus: booking?.status as 'pending' | 'confirmed' | undefined,
            guestName: booking?.guestName,
            isCheckIn: booking
              ? date.toDateString() === booking.checkIn.toDateString()
              : false,
            isCheckOut: false,
            basePrice,
            customPrice: custom?.price,
            isWeekend,
            minNights: custom?.minNights ?? listingMinNights,
          };
        });

        if (!cancelled) setData(calendarData);
      } catch (err) {
        console.error('Failed to fetch calendar data:', err);
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchCalendarData();
    return () => {
      cancelled = true;
    };
  }, [listingId, month, fetchKey]);

  return { data, isLoading, refetch };
};

export default useCalendar;
